import { format } from 'date-fns';
import { FormProvider, useForm, type FieldErrors, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Carregando from '@/shared/components/Carregando';
import { TabDadosPessoais } from '@/modules/people/presentation/forms/form.dadospessoais';
import { OperationalProfilesSection } from '@/modules/people/presentation/forms/profiles/operational-profiles-section';
import {
  buildPerfisOperacionaisFromForm,
  syncPerfisOperacionaisToPayload,
} from '@/modules/people/presentation/forms/profiles/operational-profiles.utils';
import {
  describePersonFormValidationError,
  normalizePersonFormFromApi,
  PersonFormDto,
  shouldIncludeProfileInPayload,
} from '@/modules/people/presentation/person-form.utils';
import { buildPersonFormSchema } from '@/modules/people/presentation/person.schema';
import { readPerson, updatePerson } from '@/modules/people/person.service';
import { DataIso } from '@/shared/utils/datas';
import { validaCPF } from '@/shared/utils/cpfs';
import { validarListaAutorizacoesTrabalhoRemoto } from '@/modules/people/presentation/forms/profiles/autorizacao-trabalho-remoto.utils';
import {
  validarListaVinculosAssessores,
  validarListaVinculosDefensores,
} from '@/modules/people/presentation/forms/profiles/vinculo-assessor-defensor.utils';
import { UpdatePersonDto } from '@/api.types';
import { PermissionEnum } from '@/modules/system/auth/domain/enums/permission.enum';
import { RoleEnum } from '@/modules/system/auth/domain/enums/role.enum';
import { ValidarPermissaoUsuario } from '@/modules/system/auth/application/validate-permissions';
import MyContext from '@/shared/contexts/AppContext';
import { NavigationView } from '@/shared/constants/navigation-view.enum';
import { WizardStepActions } from '@/shared/components/forms/WizardStepActions';
import { isUsuarioAdministrador } from '@/shared/constants/visao-sistema.constants';

const PAGE_TIPO = 'todos' as const;

type Props = {
  pessoaId: string;
  onBack: () => void;
  onCancel: () => void;
  onSuccess: (data: { estagiarioId: number; estagioId: number; nome: string }) => void;
};

export function CadastroEstagiarioWizardStep({ pessoaId, onBack, onCancel, onSuccess }: Props) {
  const { authVisao } = useContext(MyContext)!;
  const podeVerDefensor = ValidarPermissaoUsuario(RoleEnum.DEFENSOR, PermissionEnum.UPDATE);
  const podeVerEstagiario = ValidarPermissaoUsuario(RoleEnum.ESTAGIARIO, PermissionEnum.UPDATE);
  const podeVerColaborador = ValidarPermissaoUsuario(RoleEnum.COLABORADOR, PermissionEnum.UPDATE);
  const podeVerUsuarios = ValidarPermissaoUsuario(RoleEnum.USUARIO, PermissionEnum.UPDATE);
  const podeGerenciarAdministrador = authVisao === NavigationView.Administrador;

  const [loaded, setLoaded] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [showAnexosModal, setShowAnexosModal] = useState(false);
  const [pessoaEhAdministrador, setPessoaEhAdministrador] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const validationContext = useMemo(
    () => ({ tipo: PAGE_TIPO, podeVerUsuarios, podeVerColaborador }),
    [podeVerUsuarios, podeVerColaborador],
  );

  const personFormSchema = useMemo(
    () => buildPersonFormSchema(validationContext),
    [validationContext],
  );

  const methods = useForm<PersonFormDto>({
    shouldUnregister: false,
    resolver: zodResolver(personFormSchema as never) as Resolver<PersonFormDto>,
    mode: 'onTouched',
    reValidateMode: 'onChange',
    shouldFocusError: false,
  });

  const cadastroAdministradorBloqueado =
    (pessoaEhAdministrador || isUsuarioAdministrador(methods.watch('usuario'))) &&
    !podeGerenciarAdministrador;

  const carregarPessoa = useCallback(async () => {
    try {
      const res = await readPerson({
        skip: 0,
        take: 1,
        id: pessoaId,
        comTelefone: true,
        comEndereco: true,
        comUsuario: true,
        comDefensor: true,
        comEstagiario: true,
        comEstagios: true,
        comColaborador: true,
      });
      const pessoa = res[0]?.[0];

      if (!pessoa) {
        toast.error('Pessoa nao encontrada.');
        return;
      }

      setPessoaEhAdministrador(isUsuarioAdministrador(pessoa.usuario));

      const formatDateInput = (value?: string | Date | null, pattern: string = 'yyyy-MM-dd') =>
        value ? DataIso(value, pattern) : '';

      const formatEstagios = (
        estagios?: NonNullable<PersonFormDto['estagiario']>['estagios'],
      ) =>
        estagios?.map((estagio) => ({
          ...estagio,
          data_inicio_estagio: formatDateInput(estagio.data_inicio_estagio),
          data_fim_estagio: formatDateInput(estagio.data_fim_estagio),
          desligamento: formatDateInput(estagio.desligamento),
          data_colacao_grau: formatDateInput(estagio.data_colacao_grau, 'yyyy-MM'),
        })) ?? [];

      const normalized = normalizePersonFormFromApi(pessoa);
      const pessoaFormatada: PersonFormDto = {
        ...normalized,
        cpf: pessoa.cpf ? validaCPF(pessoa.cpf, true) : pessoa.cpf,
        dataNascimento: pessoa.dataNascimento
          ? format(new Date(pessoa.dataNascimento), 'yyyy-MM-dd')
          : '',
        foto: pessoa.foto,
        estagiario: normalized.estagiario
          ? {
              ...normalized.estagiario,
              estagios: formatEstagios(normalized.estagiario.estagios),
            }
          : undefined,
      };
      pessoaFormatada.perfisOperacionais = buildPerfisOperacionaisFromForm(pessoaFormatada);
      pessoaFormatada.perfisOperacionaisRemovidos = [];
      methods.reset(pessoaFormatada);
    } catch (error) {
      toast.error(String(error));
    } finally {
      setLoaded(true);
    }
  }, [methods, pessoaId]);

  useEffect(() => {
    void carregarPessoa();
  }, [carregarPessoa]);

  const handleError = (fieldErrors: FieldErrors<PersonFormDto>) => {
    if (import.meta.env.DEV) {
      console.warn('[CadastroEstagiarioWizard] Validacao:', fieldErrors);
    }
    toast.error(describePersonFormValidationError(fieldErrors));
  };

  const handleSubmit = (data: PersonFormDto) => {
    if (cadastroAdministradorBloqueado) {
      toast.error('Somente um administrador pode gerenciar o cadastro de outro administrador.');
      return;
    }

    const input = {
      ...data,
      cpf: data.cpf ? data.cpf.replace(/\D/g, '') : data.cpf,
    } as PersonFormDto;

    delete input.defensoresHistorico;
    delete input.estagiariosHistorico;
    delete input.colaboradoresHistorico;

    syncPerfisOperacionaisToPayload(input);

    if (input.telefone?.numero) input.telefone.numero = input.telefone.numero.replace(/\D/g, '');
    if (input.celular?.numero) input.celular.numero = input.celular.numero.replace(/\D/g, '');

    if (!shouldIncludeProfileInPayload(input.usuario, podeVerUsuarios, true)) delete input.usuario;
    if (!shouldIncludeProfileInPayload(input.estagiario, podeVerEstagiario, true)) delete input.estagiario;
    if (!shouldIncludeProfileInPayload(input.defensor, podeVerDefensor, true)) delete input.defensor;
    if (!shouldIncludeProfileInPayload(input.colaborador, podeVerColaborador, true)) delete input.colaborador;

    if (input.colaborador) {
      input.colaborador.vinculosDefensores = (input.colaborador.vinculosDefensores ?? []).map(
        (item) => ({
          ...item,
          defensor_id: Number(item.defensor_id),
          data_inicial: DataIso(item.data_inicial),
          data_final: item.data_final ? DataIso(item.data_final) : null,
        }),
      );
      const erroVinculosDefensores = validarListaVinculosDefensores(
        input.colaborador.vinculosDefensores,
      );
      if (erroVinculosDefensores) {
        toast.error(erroVinculosDefensores);
        return;
      }
    }

    if (input.estagiario) {
      input.estagiario.estagios = input.estagiario.estagios?.map((item) => ({
        ...item,
        data_colacao_grau: DataIso(item.data_colacao_grau),
        data_inicio_estagio: DataIso(item.data_inicio_estagio),
        data_fim_estagio: DataIso(item.data_fim_estagio),
        desligamento: DataIso(item.desligamento),
      }));
    }

    if (input.defensor) {
      input.defensor.autorizacoesTrabalhoRemoto = (
        input.defensor.autorizacoesTrabalhoRemoto ?? []
      ).map((item) => ({
        ...item,
        data_inicial: DataIso(item.data_inicial),
        data_final: item.data_final ? DataIso(item.data_final) : null,
        anotacao: item.anotacao?.trim() ? item.anotacao.trim() : null,
      }));
      const erroAutorizacoes = validarListaAutorizacoesTrabalhoRemoto(
        input.defensor.autorizacoesTrabalhoRemoto,
      );
      if (erroAutorizacoes) {
        toast.error(erroAutorizacoes);
        return;
      }

      input.defensor.vinculosAssessores = (input.defensor.vinculosAssessores ?? []).map((item) => ({
        ...item,
        colaborador_id: Number(item.colaborador_id),
        data_inicial: DataIso(item.data_inicial),
        data_final: item.data_final ? DataIso(item.data_final) : null,
      }));
      const erroVinculos = validarListaVinculosAssessores(input.defensor.vinculosAssessores);
      if (erroVinculos) {
        toast.error(erroVinculos);
        return;
      }
    }

    setIsExecuting(true);
    updatePerson(input as UpdatePersonDto)
      .then(async () => {
        toast.success(`Cadastro de ${input.nome} atualizado com sucesso.`);

        const res = await readPerson({
          skip: 0,
          take: 1,
          id: pessoaId,
          comEstagiario: true,
          comEstagios: true,
        });
        const pessoa = res[0]?.[0];
        const estagiarioId = pessoa?.estagiario?.id;
        const estagios = pessoa?.estagiario?.estagios ?? [];
        const estagioAtivo = estagios.find((e) => !e.desligamento) ?? estagios[0];

        if (!estagiarioId || !estagioAtivo?.id) {
          toast.error('Nao foi possivel identificar o estagio do estagiario.');
          return;
        }

        onSuccess({
          estagiarioId,
          estagioId: estagioAtivo.id,
          nome: pessoa?.nome ?? input.nome ?? '',
        });
      })
      .catch((res) => {
        toast.error(res instanceof Error ? res.message : String(res));
      })
      .finally(() => {
        setIsExecuting(false);
      });
  };

  if (!loaded) return <Carregando />;

  return (
    <FormProvider {...methods}>
      <fieldset disabled={cadastroAdministradorBloqueado}>
        <form
          ref={formRef}
          onSubmit={methods.handleSubmit(handleSubmit, handleError)}
        >
          <TabDadosPessoais
            methods={methods}
            showAnexosModal={showAnexosModal}
            onCloseAnexosModal={() => setShowAnexosModal(false)}
          />

          <OperationalProfilesSection
            methods={methods}
            pageTipo={PAGE_TIPO}
            podeVerDefensor={podeVerDefensor}
            podeVerEstagiario={podeVerEstagiario}
            podeVerColaborador={podeVerColaborador}
          />

          <WizardStepActions
            onBack={onBack}
            secondaryLabel="Sair do fluxo"
            onSecondary={onCancel}
            primaryLabel="Salvar e continuar"
            primaryLoadingLabel="Salvando..."
            isLoading={isExecuting}
          />
        </form>
      </fieldset>
    </FormProvider>
  );
}
